import React from "react";
import { MapPin, Instagram, Phone, Heart } from "lucide-react";
import { SHOP, WHATSAPP_NUMBER } from "@/lib/api";
import { SmartImage } from "@/components/SmartImage";

export const AboutPage = () => {
  return (
    <div className="pb-24 md:pb-16" data-testid="about-page">
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 grid md:grid-cols-2 gap-8 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#D81B60] font-bold">Our Story</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#2D0A22] mt-2 leading-[1.05]">
            Handcrafted with <span className="italic text-[#D81B60]">love</span> from Vita.
          </h1>
          <p className="font-script text-2xl text-[#D81B60] mt-1">— by Aditya Bansode</p>
          <p className="mt-4 text-[#8E6377] leading-relaxed">
            Sugandhi Flowers began as a small idea in the heart of <b className="text-[#2D0A22]">Vita, Sangli</b> — to create flowers
            that never fade. Every bouquet you see here is stitched by hand, thread by thread, with a promise of joy that lasts forever.
          </p>
          <p className="mt-3 text-[#8E6377] leading-relaxed">
            From delicate blush roses to jumbo sunflower bundles, we design each piece for the moments that matter — engagements,
            anniversaries, birthdays, and every little "just because" in between.
          </p>

          <div className="mt-6 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-[#FCE4EC] text-[#D81B60] flex items-center justify-center flex-shrink-0"><MapPin size={16} /></div>
              <p className="text-sm text-[#2D0A22]"><b>Address:</b> {SHOP.address}</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-[#FCE4EC] text-[#D81B60] flex items-center justify-center flex-shrink-0"><Phone size={16} /></div>
              <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" className="text-sm text-[#2D0A22] hover:text-[#D81B60]">
                <b>WhatsApp:</b> +91 93226 30051
              </a>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-[#FCE4EC] text-[#D81B60] flex items-center justify-center flex-shrink-0"><Instagram size={16} /></div>
              <a href={SHOP.instagram} target="_blank" rel="noreferrer" className="text-sm text-[#2D0A22] hover:text-[#D81B60]">
                <b>Instagram:</b> {SHOP.instagramHandle} · {SHOP.followers} followers
              </a>
            </div>
          </div>
        </div>

        <div className="relative">
          <SmartImage
            src="https://images.unsplash.com/photo-1528698827591-e59cd55f4a7c?auto=format&fit=crop&q=80&w=1000"
            alt="Handcrafted crochet flowers"
            width={800}
            widths={[400, 600, 800, 1000]}
            sizes="(max-width: 768px) 100vw, 50vw"
            aspectRatio="4/5"
            className="w-full aspect-[4/5] object-cover rounded-3xl shadow-[0_20px_60px_rgba(216,27,96,0.15)]"
          />
          <div className="absolute -bottom-4 left-6 right-6 bg-white/90 backdrop-blur-lg rounded-2xl p-4 flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[#D81B60] text-white flex items-center justify-center"><Heart size={20} fill="currentColor" /></div>
            <div>
              <p className="font-serif text-lg text-[#2D0A22] leading-tight">20,000+ hearts</p>
              <p className="text-xs text-[#8E6377]">on Instagram · and counting</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { title: "Handmade", desc: "Each bloom stitched with care in Vita, Maharashtra." },
            { title: "Everlasting", desc: "Crochet flowers that stay beautiful for years." },
            { title: "Customizable", desc: "Design your own bouquet — sizes, flowers, colors." },
          ].map((f, i) => (
            <div key={i} className="p-6 rounded-3xl bg-white border border-[#F4D8E2] shadow-[0_4px_20px_rgba(216,27,96,0.05)]">
              <p className="font-serif text-2xl text-[#D81B60]">{f.title}</p>
              <p className="text-sm text-[#8E6377] mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
